export interface SignupAttribution {
  ref?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  timestamp?: number;
  source?: 'signup' | 'login';
}

const ATTR_STORAGE_KEY = 'utmguard_signup_attrs';

const isBrowser = typeof window !== 'undefined' && typeof localStorage !== 'undefined';

const safeParse = (value: string | null): SignupAttribution | null => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const getStoredAttribution = (): SignupAttribution | null => {
  if (!isBrowser) return null;
  return safeParse(localStorage.getItem(ATTR_STORAGE_KEY));
};

export const setStoredAttribution = (data: SignupAttribution) => {
  if (!isBrowser) return;
  localStorage.setItem(
    ATTR_STORAGE_KEY,
    JSON.stringify({
      ...data,
      timestamp: Date.now(),
      source: data.source || 'signup',
    })
  );
};

export const captureAttributionParams = (search: string) => {
  if (!isBrowser || !search) return;

  const params = new URLSearchParams(search);
  const extract = (key: string) => params.get(key)?.trim() || null;

  const nextData: SignupAttribution = {
    ref: extract('ref') || extract('ref_source') || extract('referral'),
    utm_source: extract('utm_source'),
    utm_medium: extract('utm_medium'),
    utm_campaign: extract('utm_campaign'),
    utm_content: extract('utm_content'),
    utm_term: extract('utm_term'),
  };

  const hasValue = Object.values(nextData).some(Boolean);
  if (!hasValue) return;

  const existing = getStoredAttribution() || {};
  const merged: SignupAttribution = { ...existing };

  // Only fill missing fields; do not overwrite existing attribution
  (Object.keys(nextData) as (keyof SignupAttribution)[]).forEach((key) => {
    const incoming = nextData[key];
    if (incoming && !merged[key]) {
      merged[key] = incoming;
    }
  });

  // If nothing new was added, skip writing
  const added = (Object.keys(nextData) as (keyof SignupAttribution)[]).some(
    (key) => nextData[key] && nextData[key] === merged[key] && !existing[key]
  );

  if (!added) return;

  // Never overwrite an existing source; default to signup when first set
  if (!merged.source) {
    merged.source = 'signup';
  }

  setStoredAttribution(merged);
};

export const getPromoRefFromStorage = (): string | null => {
  const data = getStoredAttribution();
  return data?.ref || null;
};

export const buildUserMetadataFromAttribution = (
  data: SignupAttribution | null
): Record<string, string> => {
  if (!data) return {};
  const metadata: Record<string, string> = {};

  const mappings: Array<[keyof SignupAttribution, string]> = [
    ['ref', 'ref_source'],
    ['utm_source', 'utm_source'],
    ['utm_medium', 'utm_medium'],
    ['utm_campaign', 'utm_campaign'],
    ['utm_content', 'utm_content'],
    ['utm_term', 'utm_term'],
  ];

  mappings.forEach(([attrKey, metaKey]) => {
    const value = data[attrKey];
    if (value) {
      metadata[metaKey] = value;
    }
  });

  return metadata;
};

export const getMissingMetadataFields = (
  existingMetadata: Record<string, any> | null | undefined,
  attribution: SignupAttribution | null
): Record<string, string> => {
  if (!attribution) return {};
  const missing: Record<string, string> = {};
  const metadata = existingMetadata || {};

  const mappings: Array<[keyof SignupAttribution, string]> = [
    ['ref', 'ref_source'],
    ['utm_source', 'utm_source'],
    ['utm_medium', 'utm_medium'],
    ['utm_campaign', 'utm_campaign'],
    ['utm_content', 'utm_content'],
    ['utm_term', 'utm_term'],
  ];

  mappings.forEach(([attrKey, metaKey]) => {
    const value = attribution[attrKey];
    if (value && !metadata[metaKey]) {
      missing[metaKey] = value;
    }
  });

  return missing;
};
